/**
 * MCOS RETAIN, the clinic's list of patients by retention risk.
 *
 * Built to the client's 23 September mockup (`docs/design-refs-2026-09-23/
 * 2-retain-list.jpeg`), which his production UI spec makes the visual source
 * of truth: "Match the latest mockup. Top KPI cards: Patients Monitored,
 * Needing Attention, Priority Alerts, Average RETAIN Score. Then filters:
 * All | Priority | High Risk | At Risk | Stable. Each patient gets one clean
 * row. Do not show internal rule IDs or every triggered alert here."
 *
 * Every size, gap and colour below was measured off the mockup at its native
 * 1536 px width, where one image pixel is one CSS pixel, so the numbers are
 * deliberately exact rather than rounded to the nearest spacing step.
 *
 * Everything on screen comes from the API: the KPI figures from the clinic
 * summary (the whole clinic, never the filtered page), the rows from the
 * server-side search, filter and pagination. The reason on each row is
 * written by the API from the engine's stored drivers; the row itself holds
 * no rule IDs. The older free-text signal board and the re-score control are
 * no longer on this screen; their endpoints are unchanged.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { ErrorState } from '../components/ui';
import {
  IconArrowRight,
  IconCalendar,
  IconChevronDown,
  IconSearch,
  IconShield,
} from '../components/icons';

/* ------------------------------------------------------------------ *
 * Shared with the patient screen
 * ------------------------------------------------------------------ */

export type RiskTier = 'STABLE' | 'WATCH' | 'AT_RISK' | 'HIGH_RISK' | 'PRIORITY_REVIEW';

/**
 * Colours sampled from the two RETAIN mockups. The spec's navy (#1C2B3A) is
 * the sidebar text; the headings, names and big figures in both mockups are
 * a deeper blue, measured here, and the muted greys likewise.
 */
export const INK = {
  heading: '#0b2447',
  strong: '#16214d',
  body: '#2b3446',
  muted: '#5c6576',
  faint: '#6e7686',
  border: '#e2e6e4',
  divider: '#eceef0',
} as const;

/**
 * The risk hierarchy, the only place red, orange and yellow appear ("use
 * red/orange/yellow sparingly and only for risk hierarchy"). Badge wording is
 * the mockup's. Watch has no filter chip in the mockup but is a real tier, so
 * it keeps the soft green of a patient who needs watching, not action.
 */
export const TIER_STYLE: Record<
  RiskTier,
  { label: string; text: string; fill: string; score: string }
> = {
  PRIORITY_REVIEW: { label: 'Priority Review', text: '#cc1a24', fill: '#fde7ea', score: '#d4111b' },
  HIGH_RISK: { label: 'High Risk', text: '#d9531c', fill: '#fce6d9', score: '#d9531c' },
  AT_RISK: { label: 'At Risk', text: '#8f6a14', fill: '#fdf0c7', score: '#8f6a14' },
  WATCH: { label: 'Watch', text: '#3e6656', fill: '#e3efe7', score: '#3e6656' },
  STABLE: { label: 'Stable', text: '#0b5345', fill: '#e3efe7', score: '#0b5345' },
};

type IconProps = { size?: number; className?: string; strokeWidth?: number };

function Svg({ size = 24, className = '', strokeWidth = 1.7, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** The mockup's warning triangle: rounded corners, a bolder line than the rail icons. */
export function IconWarning(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M10.27 4.2 2.5 17.7a2 2 0 0 0 1.73 3h15.54a2 2 0 0 0 1.73-3L13.73 4.2a2 2 0 0 0-3.46 0Z" />
      <path d="M12 9.2v4.9" />
      <circle cx="12" cy="17.25" r="0.35" fill="currentColor" />
    </Svg>
  );
}

/** Two people, the first KPI card's glyph. */
function IconPeople(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="8.6" cy="7.6" r="3.3" />
      <path d="M2.4 19.6c0-3.5 2.8-6.3 6.2-6.3s6.2 2.8 6.2 6.3" />
      <path d="M14.3 4.9a3.3 3.3 0 1 1 1.9 5.9" />
      <path d="M17.3 13.5c2.5.5 4.3 3 4.3 6.1" />
    </Svg>
  );
}

/** Rising bars under a trend arrow, the fourth KPI card's glyph. */
function IconGrowth(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.6" y="14.6" width="3.8" height="5.8" rx="0.7" />
      <rect x="10.1" y="11.2" width="3.8" height="9.2" rx="0.7" />
      <rect x="16.6" y="7" width="3.8" height="13.4" rx="0.7" />
      <path d="M3.4 11.4 7.6 7.6l3.2 1.9 6.9-6" />
      <path d="M15 3.4h2.9v2.8" />
    </Svg>
  );
}

/** Rounded score, or "n/a" when the check-in had nothing scoreable (null is not zero). */
export function scoreText(score: number | null): string {
  return score === null ? 'n/a' : String(Math.round(score));
}

/** "24.5 pts", with the decimal only when the engine stored one. */
export function pointsText(delta: number, unit: 'pts' | 'points'): string {
  const size = Math.abs(delta);
  const figure = Number.isInteger(size) ? String(size) : size.toFixed(1);
  return `${figure} ${unit}`;
}

/** The risk pill: triangle and tier name on the tier's tint. */
export function RiskBadge({
  tier,
  className = '',
}: {
  tier: RiskTier;
  className?: string;
}) {
  const style = TIER_STYLE[tier];
  return (
    <span
      className={`inline-flex h-[34px] min-w-[126px] items-center gap-[6px] rounded-full pl-[15px] pr-[18px] text-[15px] font-medium leading-none ${className}`}
      style={{ backgroundColor: style.fill, color: style.text }}
    >
      <IconWarning size={29} strokeWidth={1.75} className="-my-1 shrink-0" />
      {style.label}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Data
 * ------------------------------------------------------------------ */

interface RetainSummaryData {
  patients: number;
  counts: Record<RiskTier, number>;
  needingAttention: number;
  priorityAlerts: number;
  hardAlerts: number;
  averageScore: number | null;
}

interface BoardRow {
  id: string;
  patientId: string;
  assessmentMonth: number;
  assessmentAt: string;
  retainScore: number | null;
  riskTier: RiskTier;
  trendDirection: 'UP' | 'STABLE' | 'DOWN' | null;
  trendDelta: number | null;
  hardAlertActive: boolean;
  patient: { id: string; firstName: string; lastName: string; primaryGoal: string | null; treatments: string[] };
  context: string | null;
  reason: { title: string; detail: string | null };
}

interface BoardPage {
  assessments: BoardRow[];
  total: number;
  nextCursor: number | null;
}

/** The mockup's five chips. Watch is left out, as drawn; it stays visible under All. */
const FILTERS: { value: RiskTier | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PRIORITY_REVIEW', label: 'Priority' },
  { value: 'HIGH_RISK', label: 'High Risk' },
  { value: 'AT_RISK', label: 'At Risk' },
  { value: 'STABLE', label: 'Stable' },
];

/** Five to a page, as the mockup's pager reads ("1 to 5 of 20 patients"). */
const PAGE_SIZE = 5;

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export function RetainPage() {
  useEffect(() => {
    document.title = 'MCOS RETAIN';
  }, []);

  return (
    <div className="pb-2">
      <header>
        {/*
          44px Fraunces, not the 34px page heading: the mockup's title is set
          larger than every other page's, with the trademark raised beside it.
        */}
        <h1
          className="-mt-[1px] ml-[2px] whitespace-nowrap font-display text-[clamp(30px,9vw,45px)] leading-[1.05] tracking-[-0.005em] sm:text-[45px] sm:leading-[44px]"
          style={{ fontWeight: 560, color: INK.heading }}
        >
          MCOS RETAIN
          <span className="relative top-[1px] ml-[2px] inline-block align-top text-[clamp(17px,5.2vw,26px)] font-normal leading-none tracking-normal sm:text-[26px]">
            ™
          </span>
        </h1>
        <p className="mt-[3px] text-[18.5px] leading-[24px]" style={{ color: '#5d6677' }}>
          Know which patients need attention before they leave.
        </p>
      </header>

      <RetainKpis />
      <RetainBoard />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The four KPI cards
 * ------------------------------------------------------------------ */

/**
 * Clinic-wide figures, from their own request. The board below is filtered
 * and paged, and counting its rows would describe one page under a label that
 * says "monitored".
 */
function RetainKpis() {
  const query = useQuery({
    queryKey: ['retain', 'summary'],
    queryFn: () => api.get<RetainSummaryData>('/retain/assessments/summary'),
  });

  const data = query.data;
  const cards: {
    key: string;
    label: string;
    value: string;
    caption: string;
    icon: ReactNode;
    tone: 'green' | 'red';
    valueColor?: string;
  }[] = [
    {
      key: 'monitored',
      label: 'Patients Monitored',
      value: data ? String(data.patients) : '',
      caption: 'with a scored check‑in', // non-breaking hyphen: a wrapped caption never splits check-in
      icon: <IconPeople size={41} strokeWidth={1.45} />,
      tone: 'green',
    },
    {
      key: 'attention',
      label: 'Needing Attention',
      value: data ? String(data.needingAttention) : '',
      caption: 'at risk or worse',
      icon: <IconWarning size={42} strokeWidth={1.4} />,
      tone: 'red',
      valueColor: data && data.needingAttention > 0 ? TIER_STYLE.PRIORITY_REVIEW.text : undefined,
    },
    {
      key: 'priority',
      label: 'Priority Alerts',
      value: data ? String(data.priorityAlerts) : '',
      caption: data?.priorityAlerts === 1 ? 'requires immediate review' : 'require immediate review',
      icon: <IconShield size={48} className="[stroke-width:1.25]" />,
      tone: 'green',
    },
    {
      key: 'average',
      label: 'Average RETAIN Score',
      value: data ? (data.averageScore === null ? 'n/a' : String(Math.round(data.averageScore))) : '',
      caption: 'across the clinic',
      icon: <IconGrowth size={42} strokeWidth={1.35} />,
      tone: 'green',
    },
  ];

  return (
    <section aria-label="RETAIN summary" className="mt-[24px] grid grid-cols-2 gap-3 xl:grid-cols-4">
      {cards.map((card) => (
        <div
          key={card.key}
          className="flex flex-col items-start rounded-[10px] border bg-white p-4 sm:min-h-[133px] sm:flex-row sm:pb-4 sm:pl-4 sm:pr-3 sm:pt-[18px]"
          style={{ borderColor: INK.border }}
        >
          <span
            aria-hidden="true"
            className="grid h-12 w-12 shrink-0 place-items-center rounded-full max-sm:[&>svg]:h-7 max-sm:[&>svg]:w-7 sm:h-[71px] sm:w-[71px]"
            style={
              card.tone === 'red'
                ? { backgroundColor: '#fde8ea', color: '#d4111b' }
                : { backgroundColor: '#eaf5f1', color: '#0b5345' }
            }
          >
            {card.icon}
          </span>
          <div className="mt-3 min-w-0 max-w-full sm:ml-5 sm:mt-0 sm:pt-[6px]">
            <p
              title={card.label}
              className="break-words text-[12.5px] font-semibold uppercase leading-[16px] tracking-[0.03em]"
              style={{ color: '#334056' }}
            >
              {card.label}
            </p>
            {query.isPending ? (
              <span className="mt-[9px] block h-[34px] w-12 animate-pulse rounded bg-ink/5" />
            ) : query.isError ? (
              <p className="mt-[9px] text-[14px]" style={{ color: INK.muted }}>
                Could not load
              </p>
            ) : (
              <p
                className="mt-[4px] font-display text-[40px] leading-[48px]"
                style={{ fontWeight: 560, color: card.valueColor ?? INK.heading }}
              >
                {card.value}
              </p>
            )}
            <p title={card.caption} className="break-words mt-0 text-[14px] leading-[20px]" style={{ color: '#636b7a' }}>
              {card.caption}
            </p>
          </div>
        </div>
      ))}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * The board
 * ------------------------------------------------------------------ */

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function RetainBoard() {
  const [tier, setTier] = useState<RiskTier | 'ALL'>('ALL');
  const [month, setMonth] = useState<number | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const term = useDebounced(search.trim(), 250);

  // A new filter starts at page one: page four of a result with one page is
  // an empty list that looks broken.
  useEffect(() => setPage(0), [tier, month, term]);

  const query = useQuery({
    queryKey: ['retain', 'board', tier, month, term, page],
    queryFn: () =>
      api.get<BoardPage>(
        `/retain/assessments${qs({
          limit: PAGE_SIZE,
          cursor: page * PAGE_SIZE,
          tier: tier === 'ALL' ? undefined : tier,
          month: month === 'ALL' ? undefined : month,
          q: term || undefined,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });

  const rows = query.data?.assessments ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtered = tier !== 'ALL' || month !== 'ALL' || term !== '';

  return (
    <section
      aria-label="Patients by RETAIN risk"
      className="mt-[21px] overflow-hidden rounded-[10px] border bg-white"
      style={{ borderColor: INK.border }}
    >
      {/* Toolbar: search left, the segmented filter and the month on the right. */}
      <div
        className="flex flex-wrap items-center gap-3 border-b px-[13px] py-[13px]"
        style={{ borderColor: '#e9ebed' }}
      >
        <label className="relative block w-full sm:w-[370px]">
          <span className="sr-only">Search patients</span>
          <IconSearch
            size={21}
            className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-[#1f5a4e] [stroke-width:2.3]"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search patients..."
            className="h-[42px] w-full rounded-[8px] border bg-white pl-[46px] pr-3 text-[14.5px] placeholder:text-[#6b7282] focus:border-[#9fbcb5] focus:outline-none"
            style={{ borderColor: '#dde2e6', color: INK.body }}
          />
        </label>

        <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:ml-auto sm:w-auto sm:gap-[27px]">
          <div
            role="group"
            aria-label="Filter by risk"
            className="no-scrollbar flex h-[38px] max-w-full items-center rounded-[8px] bg-[#f1f5f5] max-sm:overflow-x-auto"
          >
            {FILTERS.map((filter) => {
              const active = tier === filter.value;
              return (
                <button
                  key={filter.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTier(filter.value)}
                  className={`h-[38px] shrink-0 whitespace-nowrap rounded-[8px] px-[14px] text-[15px] font-medium transition-colors sm:px-[19px] ${
                    active ? 'bg-forest text-white' : 'text-[#2b4f48] hover:text-forest'
                  }`}
                  style={{ minWidth: filter.value === 'ALL' ? 67 : undefined }}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          <MonthSelect value={month} onChange={setMonth} />
        </div>
      </div>

      {query.isPending ? (
        <div className="space-y-3 px-[15px] py-6" aria-busy="true" aria-label="Loading">
          {Array.from({ length: PAGE_SIZE }, (_, index) => (
            <div key={index} className="h-[70px] animate-pulse rounded-[8px] bg-[#f4f6f5]" />
          ))}
        </div>
      ) : query.isError ? (
        <div className="p-5">
          <ErrorState error={query.error} retry={() => void query.refetch()} />
        </div>
      ) : rows.length === 0 ? (
        <div className="px-6 py-14 text-center">
          <p className="font-display text-[18px]" style={{ fontWeight: 560, color: INK.heading }}>
            {filtered ? 'No patients match' : 'No check-ins scored yet'}
          </p>
          <p className="mx-auto mt-1.5 max-w-md text-[14.5px]" style={{ color: INK.muted }}>
            {filtered
              ? 'Try another name, risk level or month.'
              : "Monthly check-ins appear here from month 2 of a patient's cycle."}
          </p>
        </div>
      ) : (
        <ul className={`mcos-stagger px-[15px] ${query.isFetching ? 'opacity-70 transition-opacity' : ''}`}>
          {rows.map((row, index) => (
            <BoardRowItem key={row.id} row={row} showLabel={index === 0} last={index === rows.length - 1} />
          ))}
        </ul>
      )}

      {total > 0 && (
        <div
          className="flex min-h-[71px] flex-wrap items-center justify-between gap-3 border-t pl-[18px] pr-[25px] pt-[3px]"
          style={{ borderColor: '#e9ebed' }}
        >
          <p className="text-[14.5px]" style={{ color: INK.faint }}>
            {page * PAGE_SIZE + 1} to {page * PAGE_SIZE + rows.length} of {total} {total === 1 ? 'patient' : 'patients'}
          </p>
          <Pager page={page} pageCount={pageCount} onChange={setPage} />
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * One row
 * ------------------------------------------------------------------ */

/**
 * "Patient name, secondary context such as treatment/goal, RETAIN score, one
 * risk badge, one primary reason, monthly change, View Patient" (UI spec,
 * Patient Lists), in that order, on fixed columns so the scores and badges
 * line up down the page.
 */
function BoardRowItem({ row, showLabel, last }: { row: BoardRow; showLabel: boolean; last: boolean }) {
  const style = TIER_STYLE[row.riskTier];
  const name = `${row.patient.firstName} ${row.patient.lastName}`;
  return (
    <li
      className={`grid grid-cols-[48px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 py-4 lg:min-h-[96px] lg:grid-cols-[88px_minmax(0,245fr)_112px_180px_minmax(0,284fr)_142px_150px] lg:gap-0 lg:py-0 ${
        last ? '' : 'border-b'
      }`}
      style={{ borderColor: INK.divider }}
    >
      <span
        aria-hidden="true"
        className="col-start-1 row-start-1 grid h-12 w-12 place-items-center rounded-full font-display text-[18px] lg:ml-[2px] lg:h-[60px] lg:w-[60px] lg:text-[23px]"
        style={{ backgroundColor: '#e7f3ef', color: '#1f5648', fontWeight: 480 }}
      >
        {`${row.patient.firstName.charAt(0)}${row.patient.lastName.charAt(0)}`.toUpperCase()}
      </span>

      <div className="col-start-2 row-start-1 min-w-0 pt-[1px] lg:pr-3">
        <Link
          to={`/retain/${row.patientId}`}
          title={name}
          className="block break-words font-display text-[19px] leading-[24px] tracking-[-0.01em] hover:underline lg:truncate"
          style={{ fontWeight: 560, color: INK.strong }}
        >
          {name}
        </Link>
        <p
          className="mt-[4px] text-[14.5px] leading-[20px] lg:truncate"
          style={{ color: '#6a7283' }}
          title={row.context ?? undefined}
        >
          {row.context ?? 'No goal or treatment on file'}
        </p>
      </div>

      <div className="col-start-3 row-start-1 min-w-0 justify-self-end pt-[4px] text-right lg:justify-self-auto lg:text-left">
        {showLabel && (
          <p
            className="mb-[1px] text-[12px] font-medium uppercase leading-[15px] tracking-[0.035em]"
            style={{ color: '#5e6776' }}
          >
            RETAIN Score
          </p>
        )}
        <p className="flex items-baseline justify-end whitespace-nowrap lg:justify-start">
          <span
            className="font-display text-[36px] leading-[40px]"
            style={{ fontWeight: 560, color: style.score }}
          >
            {scoreText(row.retainScore)}
          </span>
          <span className="ml-[7px] text-[16.5px]" style={{ color: '#6d7384' }}>
            /100
          </span>
        </p>
      </div>

      <div className="col-span-2 col-start-1 row-start-2 lg:col-span-1 lg:col-start-4 lg:row-start-1 lg:mb-[22px]">
        <RiskBadge tier={row.riskTier} />
      </div>

      <div className="col-span-3 col-start-1 row-start-3 min-w-0 pt-[4px] lg:col-span-1 lg:col-start-5 lg:row-start-1 lg:pr-[10px]">
        <p title={row.reason.title} className="text-[16px] font-medium leading-[22px] lg:truncate" style={{ color: INK.strong }}>
          {row.reason.title}
        </p>
        {row.reason.detail && (
          <p title={row.reason.detail} className="mt-[4px] text-[14.5px] leading-[20px] lg:truncate" style={{ color: '#6a7283' }}>
            {row.reason.detail}
          </p>
        )}
      </div>

      <div className="col-start-3 row-start-2 justify-self-end lg:col-start-6 lg:row-start-1 lg:justify-self-auto">
        <MonthlyChange delta={row.trendDelta} />
      </div>

      <Link
        to={`/retain/${row.patientId}`}
        aria-label={`View ${name}`}
        className="col-span-3 col-start-1 row-start-4 inline-flex h-[42px] w-full items-center justify-between lg:col-span-1 lg:col-start-7 lg:row-start-1 lg:w-[150px] rounded-[8px] border bg-white pl-[20px] pr-[15px] text-[15px] font-medium transition-colors hover:bg-mint"
        style={{ borderColor: '#c3d7d4', color: '#174c42' }}
      >
        View Patient
        <IconArrowRight size={20} className="[stroke-width:2.1]" />
      </Link>
    </li>
  );
}

/** The thin arrow beside a monthly change, drawn to the mockup's 13 by 19. */
export function ChangeArrow({ down, width = 14, height = 20 }: { down: boolean; width?: number; height?: number }) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 14 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={down ? '' : 'rotate-180'}
    >
      <path d="M7 1.5v16.5M1.5 12.5 7 18l5.5-5.5" />
    </svg>
  );
}

/** The monthly change column: direction arrow, size in points, "this month". */
function MonthlyChange({ delta }: { delta: number | null }) {
  if (delta === null) {
    return (
      <p className="text-[14px] lg:pl-[28px]" style={{ color: INK.faint }}>
        First check-in
      </p>
    );
  }
  if (delta === 0) {
    return (
      <p className="text-[14px] lg:pl-[28px]" style={{ color: INK.faint }}>
        No change
      </p>
    );
  }
  const down = delta < 0;
  const color = down ? '#c9262e' : '#0b5345';
  return (
    <div className="flex items-start pt-[4px]">
      <span className="mt-[1px] shrink-0" style={{ color }}>
        <ChangeArrow down={down} />
      </span>
      <span className="sr-only">{down ? 'Down' : 'Up'}</span>
      <div className="ml-[14px]">
        <p className="whitespace-nowrap text-[18px] font-medium leading-[22px]" style={{ color }}>
          {pointsText(delta, 'pts')}
        </p>
        <p className="mt-[3px] text-[13.5px] leading-[18px]" style={{ color: INK.faint }}>
          this month
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Month filter
 * ------------------------------------------------------------------ */

function MonthSelect({
  value,
  onChange,
}: {
  value: number | 'ALL';
  onChange: (next: number | 'ALL') => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const options = useMemo(
    () => [
      { value: 'ALL' as const, label: 'All months' },
      ...[2, 3, 4, 5, 6].map((month) => ({ value: month, label: `Month ${month}` })),
    ],
    [],
  );
  const current = options.find((option) => option.value === value) ?? options[0]!;

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter by check-in month"
        onClick={() => setOpen((was) => !was)}
        className="flex h-[39px] w-[184px] items-center rounded-[8px] border bg-white pl-[12px] pr-[9px] text-[14.5px] font-medium"
        style={{ borderColor: '#dde2e6', color: '#33564f' }}
      >
        <IconCalendar size={21} className="shrink-0 text-forest [stroke-width:1.9]" />
        <span className="ml-[10px] flex-1 text-left">{current.label}</span>
        <IconChevronDown
          size={17}
          className={`shrink-0 text-[#1c4038] transition-transform duration-150 [stroke-width:2.2] ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-30 mt-1 w-[184px] overflow-hidden rounded-[8px] border bg-white py-1 shadow-[0_10px_28px_rgba(20,23,28,0.12)]"
          style={{ borderColor: '#dde2e6' }}
        >
          {options.map((option) => (
            <li key={String(option.value)}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`w-full px-[15px] py-2 text-left text-[14.5px] hover:bg-mint ${
                  option.value === value ? 'font-semibold text-forest' : ''
                }`}
                style={option.value === value ? undefined : { color: INK.body }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Pager
 * ------------------------------------------------------------------ */

/** "Previous 1 2 3 4 Next", the current page a filled green circle. */
function Pager({
  page,
  pageCount,
  onChange,
}: {
  page: number;
  pageCount: number;
  onChange: (next: number) => void;
}) {
  // Every page when there are few; otherwise a window around the current one.
  const pages = useMemo(() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index);
    const start = Math.max(0, Math.min(page - 3, pageCount - 7));
    return Array.from({ length: 7 }, (_, index) => start + index);
  }, [page, pageCount]);

  return (
    <nav aria-label="Pages" className="flex items-center">
      <button
        type="button"
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
        className="mr-[17px] text-[15.5px] disabled:cursor-default"
        style={{ color: page === 0 ? '#7a8190' : INK.strong }}
      >
        Previous
      </button>
      {pages.map((index) =>
        index === page ? (
          <span
            key={index}
            aria-current="page"
            className="mx-[6px] grid h-[33px] w-[33px] place-items-center rounded-full bg-forest text-[15px] font-semibold text-white"
          >
            {index + 1}
          </span>
        ) : (
          <button
            key={index}
            type="button"
            onClick={() => onChange(index)}
            className="grid h-[33px] w-[30px] place-items-center text-[15.5px] hover:text-forest"
            style={{ color: '#5f6776' }}
          >
            {index + 1}
          </button>
        ),
      )}
      <button
        type="button"
        disabled={page + 1 >= pageCount}
        onClick={() => onChange(page + 1)}
        className="ml-[14px] text-[15.5px] font-semibold disabled:cursor-default"
        style={{ color: page + 1 >= pageCount ? '#7a8190' : '#22314d' }}
      >
        Next
      </button>
    </nav>
  );
}
